import { Button } from '@/components'

/**
 * The next step offered under a freshly generated strategy, as buttons rather than prose.
 *
 * There is no walkthrough TOOL, so the AI cannot offer one as a tool call — it is a mode of the
 * Create tab. This prompt is what stands in for that: the app makes the offer the AI cannot.
 *
 * Deliberately borderless (no card, no icon, no shadow): it reads as the assistant still
 * talking, with the answer as a button, not as a result card announcing itself. The bordered
 * card treatment belongs to things that carry data — see BacktestResultCard.
 *
 * Two variants because a walkthrough replays the period the user DREW on, so it is impossible on
 * a chart with nothing on it. Someone who worked purely through the chat gets the backtest offer
 * instead — the one next step they can actually take.
 */

const COPY = {
  walkthrough: {
    text:
      "Shall we walkthrough the trades? I'll replay your trades on the chart one by one, " +
      'so you can see how the code would have played each of them out.',
    primary: 'Run walkthrough test',
  },
  backtest: {
    text: 'Want to run a backtest to see how this would have performed?',
    primary: 'Run a backtest',
  },
  // Auto-verify already ran the walkthrough and the AI accepted it: nothing to start,
  // only something to watch.
  walkthroughReady: {
    text:
      'The walkthrough is ready. Replay it on the chart to watch each trade the code took ' +
      'against your drawings.',
    primary: 'View walkthrough',
  },
} as const

export type CodeReadyVariant = keyof typeof COPY

/** Scroll anchor: BotChat brings the prompt into view when it appears — see there for why the
 *  scroll cannot live in this component. */
export const CODE_CTA_ANCHOR = 'code-ready'

interface CodeReadyPromptProps {
  variant: CodeReadyVariant
  onPrimary?: () => void
  /** Opens the Code tab. Pointedly does NOT consume the offer — looking is not answering. */
  onViewCode?: () => void
  /** Chat is busy — a new message can't be sent right now. */
  actionsDisabled?: boolean
}

export default function CodeReadyPrompt({
  variant,
  onPrimary,
  onViewCode,
  actionsDisabled = false,
}: CodeReadyPromptProps) {
  const copy = COPY[variant]

  return (
    <div data-chat-cta={CODE_CTA_ANCHOR} className="text-body text-foreground">
      <div className="mb-2">{copy.text}</div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          className="h-7 text-label"
          disabled={actionsDisabled}
          onClick={onPrimary}
        >
          {copy.primary}
        </Button>
        {/* Not gated on actionsDisabled: `actionsDisabled` means "cannot send a message", and
            this one only changes tab. Reading the code while a reply streams is fair game. */}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 text-label text-muted-foreground hover:text-foreground"
          onClick={onViewCode}
        >
          View code
        </Button>
      </div>
    </div>
  )
}
